import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { Order } from '../types';
import SimpleFormModal from '../components/SimpleFormModal';
import './PurchaseHistory.css';

const PurchaseHistory: React.FC = () => {
  const { success, error: showError, warning } = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [returning, setReturning] = useState<boolean>(false);
  const [returnRequest, setReturnRequest] = useState<{ orderId: number; orderItemId: number } | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async (): Promise<void> => {
    try {
      const response = await api.get<Order[]>('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReturn = async (orderId: number, orderItemId: number, reason: string): Promise<void> => {
    if (!reason.trim()) {
      warning('Return reason is required.');
      return;
    }
    setReturning(true);
    try {
      await api.post('/returns', {
        orderId,
        orderItemId,
        reason,
        quantity: 1
      });

      success('Return request submitted successfully!');
      fetchOrders();
      setReturnRequest(null);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to submit return request');
    } finally {
      setReturning(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'var(--color-success)';
      case 'processing':
      case 'shipped':
        return 'var(--color-teal)';
      case 'cancelled':
        return 'var(--color-danger)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const canReturnItem = (order: Order): boolean => {
    const deliveredDate = order.updatedAt || order.createdAt;
    const daysSinceDelivery = (new Date().getTime() - new Date(deliveredDate).getTime()) / (1000 * 60 * 60 * 24);
    return order.status === 'Delivered' && daysSinceDelivery <= 30;
  };

  if (loading) {
    return <div className="loading">Loading purchase history...</div>;
  }

  return (
    <div className="purchase-history-page">
      <div className="container">
        <h1 className="page-title">Purchase History</h1>

        {orders.length === 0 ? (
          <div className="no-orders">
            <p>You haven't placed any orders yet.</p>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <div>
                    <h3>Order #{order.orderNumber || order.id}</h3>
                    <p className="order-date">
                      Placed on {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="order-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                  </div>
                </div>

                <div className="order-items">
                  {order.orderItems.map((item) => {
                    const variantOptions = item.productVariant?.options || {};
                    
                    return (
                      <div key={item.id} className="order-item">
                        <div className="order-item-image">
                          <img
                            src={item.product.croppedImageUrl || item.product.imageUrl || '/placeholder-image.jpg'}
                            alt={item.product.name}
                          />
                        </div>
                        <div className="order-item-details">
                          <h4>{item.product.name}</h4>
                          {item.product.sku && (
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>SKU: {item.product.sku}</p>
                          )}
                          {item.productVariant?.sku && (
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Variant SKU: {item.productVariant.sku}</p>
                          )}
                          {Object.keys(variantOptions).length > 0 && (
                            <div style={{ marginTop: '5px' }}>
                              {Object.entries(variantOptions).map(([key, value]) => (
                                <p key={key} style={{ fontSize: '14px', margin: '2px 0' }}>
                                  {key}: {value}
                                </p>
                              ))}
                            </div>
                          )}
                          {!item.productVariant && (
                            <>
                              {item.product.size && <p>Size: {item.product.size}</p>}
                              {item.product.material && <p>Material: {item.product.material}</p>}
                            </>
                          )}
                          <p>Quantity: {item.quantity}</p>
                          <p className="order-item-price">
                            ${item.totalPrice.toFixed(2)}
                          </p>
                          {canReturnItem(order) && (
                            <button
                              onClick={() => setReturnRequest({ orderId: order.id, orderItemId: item.id })}
                              className="btn btn-secondary btn-small"
                              style={{ marginTop: '10px' }}
                            >
                              Request Return
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="order-footer">
                  <div className="order-total">
                    <strong>Total: ${order.totalAmount.toFixed(2)}</strong>
                    {order.discountAmount && order.discountAmount > 0 && (
                      <p style={{ fontSize: '14px', color: 'var(--color-success)', marginTop: '5px' }}>
                        Discount: -${order.discountAmount.toFixed(2)}
                        {order.couponCode && ` (${order.couponCode})`}
                      </p>
                    )}
                  </div>
                  {order.shippingAddress && (
                    <div className="shipping-info">
                      <p>
                        <strong>Shipping to:</strong> {order.shippingAddress}, {order.shippingCity},{' '}
                        {order.shippingState} {order.shippingZipCode}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SimpleFormModal
        isOpen={returnRequest !== null}
        title="Request Return"
        fields={[
          { name: 'reason', label: 'Return Reason', type: 'textarea', required: true, placeholder: 'Tell us why you want to return this item.' }
        ]}
        submitLabel="Submit Return"
        loading={returning}
        onSubmit={(values) => {
          if (returnRequest) {
            handleRequestReturn(returnRequest.orderId, returnRequest.orderItemId, values.reason || '');
          }
        }}
        onClose={() => setReturnRequest(null)}
      />
    </div>
  );
};

export default PurchaseHistory;

